import { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, MinusCircle, TrendingDown, MapPin, Package, Calendar, Loader2 } from 'lucide-react';
import { Bid, BidRequest, BID_STATUS_CONFIG } from '../types/bidding';
import { useAuth } from '../contexts/AuthContext';
import { bidService } from '../services/bidService';
import { driverService } from '../services/driverService';
import { hasUserRole } from '../utils/userRoles';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';

interface BidWithRequest extends Bid {
  request?: BidRequest;
  carrierCompanyName?: string;
  trailerPlate?: string;
}

export function MyBids() {
  const { user } = useAuth();
  const [myBids, setMyBids] = useState<BidWithRequest[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMyBids();
  }, [user]);

  const loadMyBids = async () => {
    try {
      setLoading(true);
      setError(null);
      let response;
      if (hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user') || isCorporateExternalTransportMobileUser(user)) {
        const companyId = String((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id || user?.id || '');
        const applicationsResponse: any = await driverService.getApplications({ status: 'approved', all: true });
        const companyDrivers = (Array.isArray(applicationsResponse?.data) ? applicationsResponse.data : []).filter((item: any) => String(item.external_transport_account_user_id || '') === companyId && item.driver_id);
        const driverIds = Array.from(new Set(companyDrivers.map((item: any) => String(item.driver_id)).filter(Boolean)));
        response = await bidService.getAll();
        const allData = Array.isArray((response as any)?.data) ? (response as any).data : [];
        (response as any).data = allData.filter((item: any) => driverIds.includes(String(item.driver_id || '')));
      } else {
        response = await bidService.getAll({ driverId: user?.id?.toString() });
      }
      const data = response.data || response;
      if (Array.isArray(data)) {
        const bids = data.map((b: any) => ({
          id: b.id?.toString() || b.bid_id?.toString() || '',
          driverId: b.driver_id || '',
          bidRequestId: b.shipment_id || b.bid_request_id || '',
          amount: b.bid_amount || b.amount || 0,
          notes: b.notes || '',
          vehiclePlate: b.vehicle_plate || '',
          trailerPlate: b.trailer_plate || b.dorse_plaka || '',
          vehicleType: b.vehicle_type || '',
          carrierCompanyName: b.carrier_company_name || '',
          status: b.status || 'pending',
          createdAt: b.created_at || new Date().toISOString(),
          updatedAt: b.updated_at || null,
          request: {
            id: b.shipment_id || '',
            sevkKodu: b.shipment_no || '',
            loadingCity: b.loading_city || '',
            loadingPoint: b.loading_point || '',
            deliveryCity: b.delivery_city || '',
            deliveryPoint: b.delivery_point || '',
            cargoType: b.cargo_type || '',
            cargoWeight: b.cargo_weight || 0,
            cargoVolume: b.cargo_volume || 0,
            distance: b.distance || 0,
            currentLowestBid: b.current_lowest_bid || null,
          } as BidRequest,
        }));
        setMyBids(bids.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      }
    } catch (err: any) {
      console.error('Failed to load bids:', err);
      setError('Teklifler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredBids = myBids.filter(bid => {
    if (filter === 'all') return true;
    return bid.status === filter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5" />;
      case 'accepted':
        return <CheckCircle className="w-5 h-5" />;
      case 'rejected':
        return <XCircle className="w-5 h-5" />;
      case 'withdrawn':
        return <MinusCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const stats = {
    total: myBids.length,
    pending: myBids.filter(b => b.status === 'pending').length,
    accepted: myBids.filter(b => b.status === 'accepted').length,
    rejected: myBids.filter(b => b.status === 'rejected').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tekliflerim</h1>
        <p className="text-gray-600 mt-1">Verdiğiniz teklifleri görüntüleyin ve yönetin</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toplam Teklif</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Beklemede</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Kazanılan</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.accepted}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Reddedilen</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.rejected}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tümü ({stats.total})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Beklemede ({stats.pending})
          </button>
          <button
            onClick={() => setFilter('accepted')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'accepted'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Kazanılan ({stats.accepted})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'rejected'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Reddedilen ({stats.rejected})
          </button>
        </div>
      </div>

      {/* Bids List */}
      <div className="space-y-4">
        {filteredBids.map(bid => {
          const config = BID_STATUS_CONFIG[bid.status];
          const request = bid.request;

          if (!request) return null;

          return (
            <div key={bid.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{request.sevkKodu}</h3>
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
                        style={{ backgroundColor: config.bgColor, color: config.color }}
                      >
                        {getStatusIcon(bid.status)}
                        {config.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Teklif Tarihi: {new Date(bid.createdAt).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    {bid.carrierCompanyName ? <p className="mt-1 text-xs font-medium text-indigo-600">Firma: {bid.carrierCompanyName}</p> : null}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 mb-1">Teklif Tutarı</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {bid.amount.toLocaleString('tr-TR')} ₺
                    </p>
                  </div>
                </div>

                {/* Route */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium mb-1">Yükleme</p>
                    <p className="font-semibold text-blue-900">{request.loadingCity}</p>
                    <p className="text-xs text-blue-700 mt-0.5">{request.loadingPoint}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium mb-1">Teslimat</p>
                    <p className="font-semibold text-green-900">{request.deliveryCity}</p>
                    <p className="text-xs text-green-700 mt-0.5">{request.deliveryPoint}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Yük</p>
                    <p className="text-sm font-medium text-gray-900">{request.cargoType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Mesafe</p>
                    <p className="text-sm font-medium text-gray-900">{request.distance} km</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Ağırlık</p>
                    <p className="text-sm font-medium text-gray-900">
                      {request.cargoWeight.toLocaleString()} kg
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Hacim</p>
                    <p className="text-sm font-medium text-gray-900">{request.cargoVolume} m³</p>
                  </div>
                </div>

                {/* Bid Details */}
                {(bid.vehiclePlate || bid.trailerPlate || bid.vehicleType || bid.notes) && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-gray-700 mb-2">Teklif Detayları</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {bid.vehiclePlate && (
                        <div>
                          <span className="text-gray-600">Araç Plakası:</span>
                          <span className="ml-2 font-medium text-gray-900">{bid.vehiclePlate}</span>
                        </div>
                      )}
                      {bid.vehicleType && (
                        <div>
                          <span className="text-gray-600">Araç Tipi:</span>
                          <span className="ml-2 font-medium text-gray-900">{bid.vehicleType}</span>
                        </div>
                      )}
                      {bid.trailerPlate && (
                        <div>
                          <span className="text-gray-600">Dorse:</span>
                          <span className="ml-2 font-medium text-gray-900">{bid.trailerPlate}</span>
                        </div>
                      )}
                    </div>
                    {bid.notes && (
                      <div className="mt-2">
                        <span className="text-gray-600 text-sm">Notlar:</span>
                        <p className="text-sm text-gray-900 mt-1">{bid.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Competition */}
                {request.currentLowestBid && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-yellow-600 font-medium mb-1">
                          Tedarikte En Düşük Teklif
                        </p>
                        <p className="font-bold text-yellow-900 flex items-center gap-1">
                          <TrendingDown className="w-4 h-4" />
                          {request.currentLowestBid.toLocaleString('tr-TR')} ₺
                        </p>
                      </div>
                      {bid.amount === request.currentLowestBid && bid.status === 'pending' && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          🏆 En Düşük Teklif Sizin!
                        </span>
                      )}
                      {bid.amount > request.currentLowestBid && bid.status === 'pending' && (
                        <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                          Rakip Önde
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {bid.status === 'accepted' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Tebrikler! Tedarik Görevini Kazandınız 🎉</p>
                        <p className="text-sm text-green-700 mt-1">
                          Bu sevkiyat size atandı. "Aktif İşlerim" bölümünden takip edebilirsiniz.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredBids.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Teklif Bulunamadı</h3>
          <p className="text-gray-600">
            {filter === 'all'
              ? 'Henüz hiç teklif vermediniz.'
              : `${BID_STATUS_CONFIG[filter]?.label || ''} durumunda teklif bulunmuyor.`}
          </p>
        </div>
      )}
    </div>
  );
}
